/**
 * RelatedArticlesRenderer — компонент для рендеринга связанных статей.
 *
 * Загружает проектную view-реализацию через `@hooks/relatedArticlesView`,
 * маршрутизирует на core-default если проект не переопределяет.
 *
 * Особенности:
 * - Ссылки в rendered контейнере используют нормальную HTML-навигацию (href).
 * - Guard от двойной монтировки через data-атрибут.
 * - Не вмешивается в кликhandling вложенных React-компонентов.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ArticleService } from '../../services/articleService';
/**
 * Loads the project-specific related articles view hook.
 * Falls back to the core default if no project override exists.
 *
 * @returns Promise resolving to the view contract with React components
 */
async function getRelatedArticlesViewContract() {
    try {
        const module = await import('@hooks/relatedArticlesView');
        return module.default();
    }
    catch (error) {
        console.warn('[RelatedArticlesRenderer] Failed to load project hook, falling back to core:', error);
        const module = await import('@core/hooks/relatedArticlesView');
        return module.default();
    }
}
/**
 * Loads the related-tags view contract.
 * Uses the @hooks/relatedTagsView alias (project override → core default).
 */
async function getRelatedTagsViewContract() {
    try {
        const module = await import('@hooks/relatedTagsView');
        return module.default();
    }
    catch (error) {
        console.warn('[RelatedArticlesRenderer] Failed to load tags hook, falling back to core:', error);
        const module = await import('@core/hooks/relatedTagsView');
        return module.default();
    }
}
export class RelatedArticlesRenderer {
    constructor() {
        this.articleService = new ArticleService();
    }
    /**
     * Fetches and renders related articles using the project-specific view hook.
     * Replaces or inserts the related articles block after the main article element.
     *
     * @param articleId - ID of the current article
     */
    async render(articleId) {
        if (!articleId) {
            console.warn('[RelatedArticlesRenderer] No articleId provided, skipping render');
            return;
        }
        const response = await this.articleService.getRelatedArticles(Number(articleId));
        if (response.state !== 'SUCCESS' || !Array.isArray(response.returnValue)) {
            console.warn('[RelatedArticlesRenderer] Unexpected response:', response);
            return;
        }
        const articles = response.returnValue;
        const skeleton = document.getElementById('related-articles-skeleton');
        if (skeleton) {
            skeleton.classList.add('d-none');
        }
        if (!articles.length) {
            return;
        }
        const viewContract = await getRelatedArticlesViewContract();
        const { ContainerComponent, CardComponent, messages } = viewContract;
        const articleElem = document.querySelector('article');
        if (!articleElem || !articleElem.parentNode) {
            console.warn('[RelatedArticlesRenderer] Article element not found');
            return;
        }
        let containerElement = document.querySelector('.related-articles-block');
        if (!containerElement) {
            containerElement = document.createElement('div');
            containerElement.className = 'related-articles-container';
            articleElem.parentNode.insertBefore(containerElement, articleElem.nextSibling);
        }
        // Guard against double-mounting
        if (containerElement.hasAttribute(RelatedArticlesRenderer.INIT_ATTR)) {
            return;
        }
        containerElement.setAttribute(RelatedArticlesRenderer.INIT_ATTR, '1');
        containerElement.classList.remove('d-none');
        containerElement.innerHTML = '';
        const tagsContract = await getRelatedTagsViewContract();
        const root = ReactDOM.createRoot(containerElement);
        const jsxElement = React.createElement(ContainerComponent, {
            items: articles,
            isLoading: false,
            CardComponent,
            messages,
            tagsContract,
        });
        root.render(jsxElement);
    }
}
/** Guard against double-mounting the same container. */
RelatedArticlesRenderer.INIT_ATTR = 'data-rel-articles-init';
